// src/pages/business/ReportsPage.tsx
// Exact replica of mobile BusinessReportsScreen.tsx
import React, { useState, useEffect, useCallback } from 'react';
import { useParams } from 'react-router-dom';
import {
  BarChart2, Package, Users, Wallet, Archive, Tag, DollarSign,
  Loader2, Search, X, ChevronRight, AlertTriangle,
} from 'lucide-react';
import {
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer,
  PieChart, Pie, Cell, Legend,
} from 'recharts';
import apiClient from '@/services/apiClient';
import BusinessPageLayout from '@/components/business/BusinessPageLayout';

type Tab = 'Overview' | 'Inventory' | 'Products' | 'Customers' | 'Revenue' | 'Storage';
const TABS: Tab[] = ['Overview','Inventory','Products','Customers','Revenue','Storage'];

const TAB_ICONS: Record<Tab, React.ReactNode> = {
  Overview:  <BarChart2 size={18} />,
  Inventory: <Package size={18} />,
  Products:  <Tag size={18} />,
  Customers: <Users size={18} />,
  Revenue:   <DollarSign size={18} />,
  Storage:   <Archive size={18} />,
};

const fmt = (n: number) => `KES ${n.toLocaleString(undefined, { maximumFractionDigits: 0 })}`;

// ── Shared sub-components ─────────────────────────────────────────────────────
const StatCard: React.FC<{ icon: React.ReactNode; value: string | number; label: string; iconColor?: string }> = ({ icon, value, label, iconColor = '#4F46E5' }) => (
  // statCard: bg white, borderRadius 12, padding 15, width ~50%, alignItems center
  <div className="flex flex-col items-center p-4 rounded-xl bg-light-card dark:bg-dark-card shadow-sm" style={{ width: 'calc(50% - 6px)', marginBottom: 10 }}>
    <div style={{ color: iconColor }}>{icon}</div>
    {/* statValue: fontSize 20, fontWeight bold, mt 8 */}
    <p className="font-bold mt-2 text-center" style={{ fontSize: 20, color: '#0F172A' }}>{value}</p>
    {/* statLabel: fontSize 12, color #718096, mt 4, textAlign center */}
    <p className="text-center mt-1" style={{ fontSize: 12, color: '#718096' }}>{label}</p>
  </div>
);

const ChartCard: React.FC<{ title: string; children: React.ReactNode }> = ({ title, children }) => (
  // chartCard: bg white, borderRadius 12, padding 15, mb 15
  <div className="bg-light-card dark:bg-dark-card rounded-xl p-4 mb-4 shadow-sm">
    {/* cardTitle: fontSize 18, fontWeight bold, color #006884, mb 15 */}
    <p className="font-bold mb-4" style={{ fontSize: 18, color: '#006884' }}>{title}</p>
    {children}
  </div>
);

const DetailRow: React.FC<{ label: string; value: React.ReactNode; bold?: boolean; color?: string }> = ({ label, value, bold, color }) => (
  // detailRow: row, justifyContent space-between, alignItems center, py 8
  <div className="flex items-center justify-between py-2">
    <span style={{ fontSize: 14, color: '#4a5568' }}>{label}</span>
    <span className={bold ? 'font-bold' : 'font-semibold'} style={{ fontSize: bold ? 16 : 14, color: color || (bold ? '#006884' : '#2d3748') }}>{value}</span>
  </div>
);

const HealthBar: React.FC<{ label: string; value: number; color: string }> = ({ label, value, color }) => (
  // healthItem: mb 20
  <div className="mb-5">
    {/* healthLabel: fontSize 14, fontWeight 600, color #4a5568, mb 8 */}
    <p className="font-semibold mb-2" style={{ fontSize: 14, color: '#4a5568' }}>{label}</p>
    {/* healthBar: h 12, bg #e2e8f0, borderRadius 6, overflow hidden, mb 6 */}
    <div className="h-3 rounded-full overflow-hidden mb-1.5" style={{ backgroundColor: '#e2e8f0' }}>
      <div className="h-full rounded-full" style={{ width: `${Math.min(value, 100)}%`, backgroundColor: color }} />
    </div>
    {/* healthValue: fontSize 13, color #718096, fontWeight 600 */}
    <p className="font-semibold" style={{ fontSize: 13, color: '#718096' }}>{Math.round(value)}%</p>
  </div>
);

const PIE_COLORS = ['#10B981','#F59E0B','#EF4444','#3B82F6','#8B5CF6'];

// ── Screen ────────────────────────────────────────────────────────────────────
export const ReportsPage: React.FC = () => {
  const { businessId } = useParams<{ businessId: string }>();
  const [tab, setTab] = useState<Tab>('Overview');
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [productSearch, setProductSearch] = useState('');

  const [inventory, setInventory] = useState<any>(null);
  const [topProducts, setTopProducts] = useState<any[]>([]);
  const [customers, setCustomers] = useState<any>(null);
  const [revenue, setRevenue] = useState<any>(null);
  const [storage, setStorage] = useState<any>(null);

  const fetchAll = useCallback(async () => {
    try {
      setLoading(true);
      const invRes = await apiClient.get(`/businesses/products/${businessId}/inventory-stats`);
      const summary = invRes.data.summary;
      const products: any[] = invRes.data.products || [];

      const potentialProfit = summary.total_retail_value - summary.total_inventory_value;
      setInventory({ ...summary, potential_profit: potentialProfit });

      const sorted = products.sort((a, b) => b.total_revenue - a.total_revenue).slice(0, 10).map(p => ({
        id: p.id, name: p.name, total_sold: p.total_sold, total_revenue: p.total_revenue,
        total_profit: p.total_profit,
        profit_margin: p.total_revenue > 0 ? (p.total_profit / p.total_revenue) * 100 : 0,
        sales_count: p.sales_count,
      }));
      setTopProducts(sorted);

      try {
        const cRes = await apiClient.get(`/businesses/${businessId}/customers/metrics`);
        setCustomers({
          total_customers: cRes.data.total_customers,
          active_customers: cRes.data.active_customers,
          new_customers_this_month: 0,
          customer_retention_rate: cRes.data.customer_retention_rate,
          repeat_purchase_rate: cRes.data.total_customers > 0 ? (cRes.data.repeat_customers / cRes.data.total_customers) * 100 : 0,
        });
      } catch {
        setCustomers({ total_customers: 0, active_customers: 0, new_customers_this_month: 0, customer_retention_rate: 0, repeat_purchase_rate: 0 });
      }

      const totalRevenue = products.reduce((s, p) => s + (p.total_revenue || 0), 0);
      const catRev = products.reduce((acc: any, p) => {
        const c = p.category || 'Uncategorized';
        acc[c] = (acc[c] || 0) + (p.total_revenue || 0);
        return acc;
      }, {});
      const topCats = Object.entries(catRev).sort(([,a]: any, [,b]: any) => b - a).slice(0, 5).map(([category, rev]) => ({ category, revenue: Number(rev) }));
      setRevenue({ monthly_revenue: totalRevenue, yearly_revenue: totalRevenue * 12, top_revenue_categories: topCats });

      const dead = products.filter(p => p.total_sold === 0);
      const slow = products.filter(p => p.stock_status === 'good' && p.total_sold < 5);
      setStorage({
        storage_cost_per_day: summary.total_inventory_value * 0.001,
        storage_efficiency: products.length > 0 ? ((products.length - dead.length) / products.length) * 100 : 0,
        slow_moving_stock_value: slow.reduce((s: number, p: any) => s + p.quantity_in_stock * p.cost_price, 0),
        dead_stock_count: dead.length,
        turnover_rate: products.length > 0 ? products.reduce((s, p) => s + (p.quantity_in_stock > 0 ? p.total_sold / p.quantity_in_stock : 0), 0) / products.length : 0,
      });
    } catch { /* silent */ }
    finally { setLoading(false); setRefreshing(false); }
  }, [businessId]);

  useEffect(() => { fetchAll(); }, [fetchAll]);

  const filteredProducts = productSearch
    ? topProducts.filter(p => p.name.toLowerCase().includes(productSearch.toLowerCase()))
    : topProducts;

  // ── Tab content ──────────────────────────────────────────────────────────────
  const renderOverview = () => (
    <>
      {/* statsGrid: row, flexWrap wrap, justifyContent space-between, mb 15 */}
      <div className="flex flex-wrap justify-between mb-4">
        <StatCard icon={<Package size={24} />} value={inventory?.total_products || 0} label="Total Products" />
        <StatCard icon={<BarChart2 size={24} />} value={fmt(revenue?.monthly_revenue || 0)} label="Monthly Revenue" iconColor="#10B981" />
        <StatCard icon={<Users size={24} />} value={customers?.total_customers || 0} label="Total Customers" iconColor="#0EA5E9" />
        <StatCard icon={<Wallet size={24} />} value={fmt(inventory?.potential_profit || 0)} label="Potential Profit" iconColor="#F59E0B" />
      </div>

      {topProducts.length > 0 && (
        <ChartCard title="Top 5 Products by Revenue">
          <ResponsiveContainer width="100%" height={220}>
            <BarChart data={topProducts.slice(0, 5).map(p => ({ name: p.name.length > 8 ? p.name.slice(0, 8) + '…' : p.name, revenue: p.total_revenue }))}>
              <CartesianGrid strokeDasharray="3 3" vertical={false} />
              <XAxis dataKey="name" tick={{ fontSize: 12 }} />
              <YAxis tick={{ fontSize: 12 }} />
              <Tooltip formatter={(v: any) => [fmt(v), 'Revenue']} />
              <Bar dataKey="revenue" fill="#4F46E5" radius={[4,4,0,0]} />
            </BarChart>
          </ResponsiveContainer>
        </ChartCard>
      )}

      <ChartCard title="Business Health Indicators">
        <HealthBar label="Inventory Health" value={inventory ? (inventory.active_products / Math.max(inventory.total_products, 1)) * 100 : 0} color="#10B981" />
        <HealthBar label="Storage Efficiency" value={storage?.storage_efficiency || 0} color="#3B82F6" />
        <HealthBar label="Customer Retention" value={customers?.customer_retention_rate || 0} color="#8B5CF6" />
      </ChartCard>
    </>
  );

  const renderInventory = () => (
    <>
      <ChartCard title="Inventory Summary">
        <DetailRow label="Total Products"       value={inventory?.total_products || 0} />
        <DetailRow label="Active Products"      value={inventory?.active_products || 0} color="#10B981" />
        <DetailRow label="Low Stock Items"      value={inventory?.low_stock_products || 0} color="#F59E0B" />
        <DetailRow label="Out of Stock"         value={inventory?.out_of_stock_products || 0} color="#EF4444" />
        <div className="border-t border-light-border dark:border-dark-border my-2" />
        <DetailRow label="Total Inventory Value" value={fmt(inventory?.total_inventory_value || 0)} bold />
        <DetailRow label="Total Retail Value"    value={fmt(inventory?.total_retail_value || 0)} bold />
        <DetailRow label="Potential Profit"      value={fmt(inventory?.potential_profit || 0)} bold color="#10B981" />
      </ChartCard>

      {inventory && (
        <ChartCard title="Stock Status Distribution">
          <ResponsiveContainer width="100%" height={220}>
            <PieChart>
              <Pie data={[
                { name: 'Active',       value: inventory.active_products,       fill: '#10B981' },
                { name: 'Low Stock',    value: inventory.low_stock_products,    fill: '#F59E0B' },
                { name: 'Out of Stock', value: inventory.out_of_stock_products, fill: '#EF4444' },
              ]} dataKey="value" cx="50%" cy="50%" outerRadius={80} label>
                {[{ fill: '#10B981' },{ fill: '#F59E0B' },{ fill: '#EF4444' }].map((e, i) => <Cell key={i} fill={e.fill} />)}
              </Pie>
              <Legend />
              <Tooltip />
            </PieChart>
          </ResponsiveContainer>
        </ChartCard>
      )}
    </>
  );

  const renderProducts = () => (
    <>
      <ChartCard title="">
        <div className="flex items-center gap-2 px-3 py-3 rounded-xl mb-3" style={{ backgroundColor: '#f7fafc' }}>
          <Search size={20} style={{ color: '#64748B' }} />
          <input value={productSearch} onChange={e => setProductSearch(e.target.value)}
            placeholder="Search products..." className="flex-1 bg-transparent text-base focus:outline-none" style={{ color: '#0F172A' }} />
          {productSearch && <button onClick={() => setProductSearch('')}><X size={20} style={{ color: '#64748B' }} /></button>}
        </div>
        <p className="text-center" style={{ fontSize: 13, color: '#718096' }}>Showing {filteredProducts.length} of {topProducts.length} products</p>
      </ChartCard>

      <ChartCard title={productSearch ? 'Search Results' : 'Top Products by Revenue'}>
        {filteredProducts.length === 0 ? (
          <div className="flex flex-col items-center py-8 gap-3">
            <Search size={48} style={{ color: '#94A3B8' }} />
            <p className="font-semibold" style={{ color: '#0F172A' }}>No products found</p>
          </div>
        ) : filteredProducts.map((p, i) => (
          // productRow: row, alignItems center, py 12, borderBottom
          <div key={p.id} className="flex items-center py-3 border-b border-light-border dark:border-dark-border last:border-0">
            {/* productRank: w40 h40, borderRadius 20, bg #006884 */}
            <div className="w-10 h-10 rounded-full flex items-center justify-center flex-shrink-0 mr-3" style={{ backgroundColor: '#006884' }}>
              <span className="font-bold text-white" style={{ fontSize: 14 }}>#{i + 1}</span>
            </div>
            <div className="flex-1 min-w-0">
              <p className="font-semibold truncate" style={{ fontSize: 15, color: '#2d3748' }}>{p.name}</p>
              <p style={{ fontSize: 12, color: '#718096' }}>Sold: {p.total_sold} | Revenue: {fmt(p.total_revenue)}</p>
            </div>
            <div className="flex items-center gap-1">
              <span className="font-bold" style={{ fontSize: 16, color: p.profit_margin > 30 ? '#10B981' : '#F59E0B' }}>{p.profit_margin.toFixed(1)}%</span>
              <ChevronRight size={20} style={{ color: '#94A3B8' }} />
            </div>
          </div>
        ))}
      </ChartCard>

      {filteredProducts.length > 0 && (
        <ChartCard title="Revenue Comparison">
          <ResponsiveContainer width="100%" height={220}>
            <BarChart data={filteredProducts.slice(0, 6).map(p => ({ name: p.name.length > 6 ? p.name.slice(0, 6) + '…' : p.name, revenue: p.total_revenue }))}>
              <CartesianGrid strokeDasharray="3 3" vertical={false} />
              <XAxis dataKey="name" tick={{ fontSize: 12 }} />
              <YAxis tick={{ fontSize: 12 }} />
              <Tooltip formatter={(v: any) => [fmt(v), 'Revenue']} />
              <Bar dataKey="revenue" fill="#4F46E5" radius={[4,4,0,0]} />
            </BarChart>
          </ResponsiveContainer>
        </ChartCard>
      )}
    </>
  );

  const renderCustomers = () => (
    customers && customers.total_customers > 0 ? (
      <>
        <ChartCard title="Customer Metrics">
          <DetailRow label="Total Customers"          value={customers.total_customers} />
          <DetailRow label="Active Customers (30 days)" value={customers.active_customers} color="#10B981" />
          <DetailRow label="New This Month"           value={customers.new_customers_this_month} color="#4F46E5" />
          <div className="border-t border-light-border dark:border-dark-border my-2" />
          <DetailRow label="Retention Rate"           value={`${customers.customer_retention_rate.toFixed(1)}%`} bold />
          <DetailRow label="Repeat Purchase Rate"     value={`${customers.repeat_purchase_rate.toFixed(1)}%`} bold />
        </ChartCard>
        <ChartCard title="Customer Engagement">
          <HealthBar label="Active Customer Rate" value={(customers.active_customers / Math.max(customers.total_customers, 1)) * 100} color="#10B981" />
          <HealthBar label="Customer Retention"   value={customers.customer_retention_rate} color="#F59E0B" />
          <HealthBar label="Repeat Purchase Rate" value={customers.repeat_purchase_rate}    color="#4F46E5" />
        </ChartCard>
      </>
    ) : (
      <ChartCard title="">
        <div className="flex flex-col items-center py-8 gap-4 text-center">
          <Users size={64} style={{ color: '#94A3B8' }} />
          <p className="font-semibold" style={{ fontSize: 16, color: '#0F172A' }}>No Customer Data Yet</p>
          <p style={{ fontSize: 14, color: '#64748B' }}>Start collecting customer phone numbers during M-Pesa payments to track loyalty and retention.</p>
          <div className="text-left w-full px-4 mt-2 space-y-2">
            {['Track repeat customers automatically','Calculate retention rates','Identify VIP customers (6+ purchases)','Analyze purchase patterns'].map(f => (
              <p key={f} style={{ fontSize: 14, color: '#64748B' }}>✓ {f}</p>
            ))}
          </div>
        </div>
      </ChartCard>
    )
  );

  const renderRevenue = () => (
    <>
      <ChartCard title="Revenue Overview">
        <DetailRow label="Daily Average"    value={fmt((revenue?.monthly_revenue || 0) / 30)} />
        <DetailRow label="Monthly Revenue"  value={fmt(revenue?.monthly_revenue || 0)} bold color="#4F46E5" />
        <DetailRow label="Projected Yearly" value={fmt(revenue?.yearly_revenue || 0)} />
      </ChartCard>

      {revenue?.top_revenue_categories?.length > 0 && (
        <ChartCard title="Revenue by Category">
          <ResponsiveContainer width="100%" height={220}>
            <PieChart>
              <Pie data={revenue.top_revenue_categories.map((c: any) => ({ name: c.category, value: c.revenue }))} dataKey="value" cx="50%" cy="50%" outerRadius={80} label>
                {revenue.top_revenue_categories.map((_: any, i: number) => <Cell key={i} fill={PIE_COLORS[i % PIE_COLORS.length]} />)}
              </Pie>
              <Legend /><Tooltip formatter={(v: any) => [fmt(v), 'Revenue']} />
            </PieChart>
          </ResponsiveContainer>
          {revenue.top_revenue_categories.map((c: any, i: number) => (
            <DetailRow key={i} label={c.category} value={fmt(c.revenue)} />
          ))}
        </ChartCard>
      )}
    </>
  );

  const renderStorage = () => (
    <>
      <ChartCard title="Storage Impact Analysis">
        <DetailRow label="Daily Storage Cost"    value={fmt(storage?.storage_cost_per_day || 0)} color="#EF4444" />
        <DetailRow label="Monthly Storage Cost"  value={fmt((storage?.storage_cost_per_day || 0) * 30)} bold color="#EF4444" />
        <div className="border-t border-light-border dark:border-dark-border my-2" />
        <DetailRow label="Storage Efficiency"    value={`${storage?.storage_efficiency?.toFixed(1)}%`} bold />
        <DetailRow label="Dead Stock Items"      value={`${storage?.dead_stock_count || 0} items`} color="#EF4444" />
        <DetailRow label="Slow Moving Stock"     value={fmt(storage?.slow_moving_stock_value || 0)} color="#F59E0B" />
        <DetailRow label="Stock Turnover Rate"   value={`${storage?.turnover_rate?.toFixed(2)}x`} bold />
      </ChartCard>

      <ChartCard title="Storage Performance">
        <HealthBar label="Overall Efficiency" value={storage?.storage_efficiency || 0}
          color={(storage?.storage_efficiency || 0) > 80 ? '#10B981' : (storage?.storage_efficiency || 0) > 60 ? '#F59E0B' : '#EF4444'} />
        {/* alertCard */}
        <div className="flex gap-3 p-3 rounded-lg mt-4" style={{ backgroundColor: '#FEF2F2', borderLeft: '4px solid #EF4444' }}>
          <AlertTriangle size={20} style={{ color: '#EF4444', flexShrink: 0 }} />
          <div>
            <p className="font-bold mb-1.5" style={{ fontSize: 14, color: '#991B1B' }}>Storage Optimization Tips</p>
            <p style={{ fontSize: 12, color: '#7F1D1D', marginBottom: 3 }}>• Clear {storage?.dead_stock_count || 0} dead stock items to free up space</p>
            <p style={{ fontSize: 12, color: '#7F1D1D', marginBottom: 3 }}>• Review slow-moving stock worth {fmt(storage?.slow_moving_stock_value || 0)}</p>
            <p style={{ fontSize: 12, color: '#7F1D1D' }}>• Improve turnover rate to reduce storage costs</p>
          </div>
        </div>
      </ChartCard>
    </>
  );

  return (
    <BusinessPageLayout title="Business Reports">
      {loading ? (
        <div className="flex justify-center py-24"><Loader2 size={36} className="animate-spin" style={{ color: '#4F46E5' }} /></div>
      ) : (
        <>
          {/* ── Tab bar — horizontal scroll, mirrors mobile ── */}
          {/* tabContainer: bg white, borderBottom, maxHeight 60 */}
          <div className="sticky top-[57px] z-30 bg-light-card dark:bg-dark-card border-b border-light-border dark:border-dark-border overflow-x-auto scrollbar-hide">
            <div className="max-w-5xl mx-auto flex px-2.5">
              {TABS.map(t => (
                <button
                  key={t}
                  onClick={() => setTab(t)}
                  // tab: row, alignItems center, py 15, px 16, mx 4, gap 6
                  className="flex items-center gap-1.5 py-4 px-4 mx-1 flex-shrink-0 font-semibold text-[14px] transition-colors"
                  style={{
                    color: tab === t ? '#006884' : '#718096',
                    borderBottom: tab === t ? '3px solid #006884' : '3px solid transparent',
                  }}
                >
                  <span style={{ color: tab === t ? '#006884' : '#718096' }}>{TAB_ICONS[t]}</span>
                  {t}
                </button>
              ))}
            </div>
          </div>

          {/* ── Tab content — scrollContainer: padding 15 ── */}
          <div className="max-w-5xl mx-auto px-4 pt-4 pb-24">
            {tab === 'Overview'   && renderOverview()}
            {tab === 'Inventory'  && renderInventory()}
            {tab === 'Products'   && renderProducts()}
            {tab === 'Customers'  && renderCustomers()}
            {tab === 'Revenue'    && renderRevenue()}
            {tab === 'Storage'    && renderStorage()}
          </div>
        </>
      )}
    </BusinessPageLayout>
  );
};

export default ReportsPage;
